import { useMutation, useQuery } from "@tanstack/react-query";
import { Booking } from "src/components/Main/Pages/Bookings/data";
import {
  CreateAnonymousBookingPayload,
  SwapBookingsPayload,
} from "src/business/booking";
import { API_URL } from "src/Utils/API";
import { mapBooking } from "../mappers/bookingMapper";

const getBookingsListQueryKey = ({ startDate, endDate, restaurantId }: { startDate?: number, endDate?: number, restaurantId?: string }) => ["restaurants", "bookings", { startDate, endDate, restaurantId }];

export const useBookingsList = ({ startDate, endDate, restaurantId }: { startDate?: number, endDate?: number, restaurantId?: string }) => {
  return useQuery({
    queryKey: getBookingsListQueryKey({ startDate, endDate, restaurantId }),
    queryFn: async () => {
      const response = await fetch(
        `${API_URL}/restaurants/${restaurantId}/bookings?startDate=${startDate}&endDate=${endDate}`, {
        credentials: "include",
      });
      const bodyData = await response.json();
      if (bodyData.success) {
        const responseBody = bodyData.data as Omit<Booking, "bookingHour">[];
        return mapBooking(responseBody);
      } else {
        throw new Error("Failed to fetch bookings");
      }
    },
    enabled: !!startDate && !!endDate,
  });
};


export interface BookingStatusUpdateResult {
  // Set by the backend when flagging a no-show pushes the booker over the
  // repeat-offender threshold: staff may then be offered to blacklist them.
  blacklistOffered: boolean;
}

export const useBookingsStatusUpdate = (restaurantId: string, onSuccess?: () => void) => {
  const updateBookingStatus = async (bookingID: string, status: string, isAnonymous: boolean): Promise<BookingStatusUpdateResult> => {
    const response = await fetch(`${API_URL}/restaurants/${restaurantId}/bookings/${bookingID}/status`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ status, isAnonymous }),
    });
    const bodyData = await response.json();
    if (!bodyData.success) {
      throw new Error("Failed to update booking status");
    }
    return { blacklistOffered: Boolean(bodyData.data?.blacklistOffered ?? bodyData.blacklistOffered) };
  };

  return useMutation({
    mutationFn: ({ bookingID, status, isAnonymous }: { bookingID: string, status: string, isAnonymous: boolean }) => updateBookingStatus(bookingID, status, isAnonymous),
    onSuccess
  })
}

export const useBlacklistBooking = (restaurantId: string, onSuccess?: () => void) => {
  return useMutation({
    mutationFn: async ({ bookingID, isAnonymous }: { bookingID: string, isAnonymous: boolean }) => {
      const response = await fetch(`${API_URL}/restaurants/${restaurantId}/bookings/${bookingID}/blacklist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ isAnonymous }),
      });
      const bodyData = await response.json();
      if (!bodyData.success) {
        throw new Error("Failed to blacklist booking");
      }
    },
    onSuccess,
  });
};

export const useUpdateBookingTable = (restaurantId: string, onSuccess?: () => void) => {
  const updateBookingTable = async (
    bookingID: string,
    assignedTable: number | null,
    isAnonymous: boolean,
    // When the booking is placed onto a slot (vs. simply re-assigned to another
    // table) the chosen slot also sets the booking's date/time.
    bookingDate?: string,
    // Offer content for the deal/event covering the destination cell. Always
    // sent (as `null` when the cell carries no offer) so the booking is
    // re-attributed to — or cleared of — the offer it lands on; the backend
    // applies both, preserving the dealContent XOR eventContent invariant.
    dealContent: string | null = null,
    eventContent: string | null = null,
  ) => {
    const body: Record<string, unknown> = {
      assignedTableNumber: assignedTable,
      isAnonymous,
      dealContent,
      eventContent,
    };
    if (bookingDate) body.bookingDate = bookingDate;

    const response = await fetch(
      `${API_URL}/restaurants/${restaurantId}/bookings/${bookingID}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );
    const bodyData = await response.json();
    if (!bodyData.success) {
      throw new Error("Failed to update booking table");
    }
  };

  return useMutation({
    mutationFn: ({
      bookingID,
      assignedTable,
      isAnonymous,
      bookingDate,
      dealContent,
      eventContent,
    }: {
      bookingID: string;
      assignedTable: number | null;
      isAnonymous: boolean;
      bookingDate?: string;
      dealContent?: string | null;
      eventContent?: string | null;
    }) =>
      updateBookingTable(
        bookingID,
        assignedTable,
        isAnonymous,
        bookingDate,
        dealContent,
        eventContent,
      ),
    onSuccess,
  });
};

export const useSwapBookings = (restaurantId: string, onSuccess?: () => void) => {
  const swapBookings = async (payload: SwapBookingsPayload) => {
    const response = await fetch(
      `${API_URL}/restaurants/${restaurantId}/bookings/swap`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );
    const bodyData = await response.json();
    if (!bodyData.success) {
      throw new Error("Failed to swap bookings");
    }
  };

  return useMutation({
    mutationFn: (payload: SwapBookingsPayload) => swapBookings(payload),
    onSuccess,
  });
};

export const useCreateAnonymousBooking = (onSuccess?: () => void, onError?: (error: Error) => void) => {
  return useMutation({
    mutationFn: async (payload: CreateAnonymousBookingPayload) => {
      const response = await fetch(`${API_URL}/anonymous-bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to create booking");
      }
      return data;
    },
    onSuccess,
    onError,
  });
}